import React, { useState, useEffect } from "react";
import styles from "./Component/CSS/NonFictional.module.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useNavigate } from "react-router-dom";
import Loading from "../common/HousGlass Loader/Loading";
import { useAudiosByGenre } from "../hooks/useAudiosHooks";
import AudioSeries from "../Homepage/components/AudioSeries";

const NonFictional = ({ categories }) => {
  const [visibleBooks, setVisibleBooks] = useState(10);
  const navigate = useNavigate();

  // Handler for when a card is clicked
  const handleCardClick = (audio) => {
    navigate(`/audio-play/${audio.slug}`);
  };

  // Handler for view all button
  const handleViewAll = (categoryId) => {
    navigate(`/short-stories/categories/${categoryId}`);
  };

  // We'll return a component for each category, conditionally rendering AudioSeries for the Series category
  return (
    <div>
      {categories && categories.length > 0 ? (
        categories.map((category) => {
          // If the category name is "Series", render the AudioSeries component
          if (category.name === "Series") {
            return <AudioSeries key={category.id} />;
          }

          // Otherwise render the normal CategorySection
          return (
            <CategorySection
              key={category.id}
              category={category}
              visibleBooks={visibleBooks}
              setVisibleBooks={setVisibleBooks}
              handleCardClick={handleCardClick}
              handleViewAll={handleViewAll}
            />
          );
        })
      ) : (
        <div className={styles.noCategories}>No categories available</div>
      )}
    </div>
  );
};

// Separate component for each category section to manage its own data loading
const CategorySection = ({
  category,
  visibleBooks,
  setVisibleBooks,
  handleCardClick,
  handleViewAll,
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const {
    data: responseData,
    isLoading,
    isError,
    error,
  } = useAudiosByGenre(category.id, { limit: visibleBooks });

  const [audios, setAudios] = useState([]);
  const [pagination, setPagination] = useState({ total: 0 });

  // Process data when it changes
  useEffect(() => {
    if (responseData) {
      // Extract the data array and pagination object
      if (responseData.data && Array.isArray(responseData.data)) {
        setAudios(responseData.data);
      }

      if (responseData.pagination) {
        setPagination(responseData.pagination);
      }
    }
  }, [responseData]);

  // Reset loading more state when data changes
  useEffect(() => {
    if (isLoadingMore && responseData) {
      setIsLoadingMore(false);
    }
  }, [responseData, isLoadingMore]);

  // Show loading state
  if (isLoading && !isLoadingMore && !audios.length) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className={styles.error}>
        Error: {error?.message || "Failed to load content"}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.boxedWrapper}>
        {/* Header: Category name on left and "View All" button on right */}
        <div className={styles.header}>
          <h2>{category.name}</h2>
          <button
            onClick={() => handleViewAll(category.id)}
            className={styles.viewAllButton}
          >
            View all &gt;
          </button>
        </div>

        {/* Audio list / Swiper container */}
        <div className={styles.bookList}>
          {audios && audios.length > 0 ? (
            <div className={styles.customSwiperNav}>
              <Swiper
                grabCursor={window.innerWidth < 768}
                freeMode={{
                  enabled: true,
                  momentum: true,
                  momentumRatio: 0.8,
                  momentumVelocityRatio: 0.8,
                  minimumVelocity: 0.02,
                }}
                modules={[Mousewheel, FreeMode]}
                mousewheel={
                  window.innerWidth >= 768
                    ? {
                        forceToAxis: true,
                        sensitivity: 1.5,
                        releaseOnEdges: true,
                      }
                    : false
                }
                speed={600}
                resistance={false}
                resistanceRatio={0}
                spaceBetween={20}
                slidesPerView={1}
                breakpoints={{
                  320: { slidesPerView: 3, spaceBetween: 8 },
                  480: { slidesPerView: 3, spaceBetween: 10 },
                  768: { slidesPerView: 3, spaceBetween: 10 },
                  1024: { slidesPerView: 7, spaceBetween: 12 },
                  1440: { slidesPerView: 7, spaceBetween: 12 },
                }}
                className={styles.swiper}
              >
                {audios.map((audio) => (
                  <SwiperSlide key={audio.id}>
                    <div
                      className={styles.bookItem}
                      onClick={() => handleCardClick(audio)}
                    >
                      <img
                        src={
                          audio.mobile_image_url ||
                          audio.computer_image_url ||
                          "/placeholder-image.jpg"
                        }
                        alt={audio.title}
                        className={styles.bookImage}
                        loading="lazy"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              {/* Custom navigation buttons */}
            </div>
          ) : (
            <div className={styles.noContent}>
              No audios available in this category
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NonFictional;
